<template>
  <div class="audit-log">
    <!-- 搜索和筛选栏 -->
    <el-row :gutter="20" style="margin-bottom: 20px;">
      <el-col :span="6">
        <el-input
          v-model="searchQuery"
          placeholder="请输入关键字搜索"
          clearable
          @keyup.enter="handleSearch"
        >
          <template #append>
            <el-button type="primary" @click="handleSearch">搜索</el-button>
          </template>
        </el-input>
      </el-col>
      <el-col :span="4">
        <el-select v-model="filterAction" placeholder="操作类型" clearable>
          <el-option label="登录" value="login" />
          <el-option label="创建" value="create" />
          <el-option label="更新" value="update" />
          <el-option label="删除" value="delete" />
        </el-select>
      </el-col>
      <el-col :span="4">
        <el-select v-model="filterTargetType" placeholder="目标类型" clearable>
          <el-option label="用户" value="user" />
          <el-option label="歌单" value="playlist" />
          <el-option label="歌曲" value="song" />
          <el-option label="设备" value="device" />
        </el-select>
      </el-col>
      <el-col :span="6">
        <el-date-picker
          v-model="dateRange"
          type="datetimerange"
          range-separator="至"
          start-placeholder="开始时间"
          end-placeholder="结束时间"
          format="YYYY-MM-DD HH:mm:ss"
          value-format="YYYY-MM-DD HH:mm:ss"
        />
      </el-col>
      <el-col :span="4">
        <el-button type="success" @click="handleSearch">筛选</el-button>
        <el-button @click="resetFilters">重置</el-button>
      </el-col>
    </el-row>

    <!-- 审计日志列表 -->
    <el-table
      :data="auditLogs"
      style="width: 100%"
      v-loading="loading"
      :header-cell-style="{ background: '#f5f7fa' }"
    >
      <el-table-column prop="id" label="ID" width="80" />
      <el-table-column prop="username" label="操作用户" min-width="120" />
      <el-table-column prop="action" label="操作类型" width="100">
        <template #default="scope">
          <el-tag :type="getActionTagType(scope.row.action)">
            {{ getActionLabel(scope.row.action) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="target_type" label="目标类型" width="100">
        <template #default="scope">
          <el-tag type="info">{{ getTargetTypeLabel(scope.row.target_type) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="target_id" label="目标ID" width="100" />
      <el-table-column prop="detail" label="详细信息" min-width="200" show-overflow-tooltip />
      <el-table-column prop="timestamp" label="操作时间" min-width="160" />
    </el-table>

    <!-- 分页 -->
    <el-pagination
      style="margin-top: 20px; text-align: right;"
      background
      layout="prev, pager, next, jumper, ->, total"
      :total="total"
      :page-size="pageSize"
      v-model:current-page="currentPage"
      @current-change="handlePageChange"
    />

    <!-- 统计信息 -->
    <el-card style="margin-top: 20px;">
      <template #header>
        <div class="card-header">
          <span>操作统计</span>
        </div>
      </template>
      <el-row :gutter="20">
        <el-col :span="6">
          <div class="stat-item">
            <div class="stat-number">{{ stats.total }}</div>
            <div class="stat-label">总操作数</div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="stat-item">
            <div class="stat-number">{{ stats.today }}</div>
            <div class="stat-label">今日操作</div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="stat-item">
            <div class="stat-number">{{ stats.users }}</div>
            <div class="stat-label">活跃用户</div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="stat-item">
            <div class="stat-number">{{ stats.loginCount }}</div>
            <div class="stat-label">登录次数</div>
          </div>
        </el-col>
      </el-row>
    </el-card>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { ElMessage } from 'element-plus';
import { getAuditLogs, getAuditStats } from '../api/audit';

export default {
  name: 'AuditLog',
  setup() {
    const auditLogs = ref([]);
    const loading = ref(false);
    const searchQuery = ref('');
    const filterAction = ref('');
    const filterTargetType = ref('');
    const dateRange = ref([]);
    const currentPage = ref(1);
    const pageSize = ref(20);
    const total = ref(0);

    const stats = reactive({
      total: 0,
      today: 0,
      users: 0,
      loginCount: 0
    });

    const loadAuditLogs = async () => {
      try {
        loading.value = true;
        const params = {
          page: currentPage.value,
          page_size: pageSize.value
        };
        
        if (searchQuery.value) {
          params.query = searchQuery.value;
        }
        if (filterAction.value) {
          params.action = filterAction.value;
        }
        if (filterTargetType.value) {
          params.target_type = filterTargetType.value;
        }
        if (dateRange.value && dateRange.value.length === 2) {
          params.start_time = dateRange.value[0];
          params.end_time = dateRange.value[1];
        }
        
        const response = await getAuditLogs(params);
        auditLogs.value = response.data || [];
        total.value = response.total || 0;
      } catch (error) {
        console.error('加载审计日志失败:', error);
        ElMessage.error('加载审计日志失败');
      } finally {
        loading.value = false;
      }
    };

    const loadStats = async () => {
      try {
        const response = await getAuditStats();
        if (response.success) {
          Object.assign(stats, response.data);
        } else {
          console.error('加载统计数据失败:', response.message);
        }
      } catch (error) {
        console.error('加载统计数据失败:', error);
      }
    };

    const handleSearch = () => {
      currentPage.value = 1;
      loadAuditLogs();
    };

    const resetFilters = () => {
      searchQuery.value = '';
      filterAction.value = '';
      filterTargetType.value = '';
      dateRange.value = [];
      currentPage.value = 1;
      loadAuditLogs();
    };

    const handlePageChange = (page) => {
      currentPage.value = page;
      loadAuditLogs();
    };

    const getActionTagType = (action) => {
      const typeMap = {
        'login': 'success',
        'create': 'primary',
        'update': 'warning',
        'delete': 'danger'
      };
      return typeMap[action] || 'info';
    };

    const getActionLabel = (action) => {
      const labelMap = {
        'login': '登录',
        'create': '创建',
        'update': '更新',
        'delete': '删除'
      };
      return labelMap[action] || action;
    };

    const getTargetTypeLabel = (targetType) => {
      const labelMap = {
        'user': '用户',
        'playlist': '歌单',
        'song': '歌曲',
        'device': '设备'
      };
      return labelMap[targetType] || targetType;
    };

    onMounted(() => {
      loadAuditLogs();
      loadStats();
    });

    return {
      auditLogs,
      loading,
      searchQuery,
      filterAction,
      filterTargetType,
      dateRange,
      currentPage,
      pageSize,
      total,
      stats,
      handleSearch,
      resetFilters,
      handlePageChange,
      getActionTagType,
      getActionLabel,
      getTargetTypeLabel
    };
  }
};
</script>

<style scoped>
.audit-log {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.stat-item {
  text-align: center;
  padding: 20px 0;
}

.stat-number {
  font-size: 24px;
  font-weight: bold;
  color: #409EFF;
  margin-bottom: 5px;
}

.stat-label {
  font-size: 14px;
  color: #666;
}
</style> 